import type { ReactNode } from 'react';
import { Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useStudy } from '../app/study';
import { api } from '../lib/api';
import { IconReplay } from '../components/watch/icons';

/**
 * F6 Practice — the launcher (design `11a`).
 *
 * "More than the bite — start anything." Today is the day's plan; this is the door to everything
 * the plan did not include, and it exists so the app never has to say no to someone who wants to
 * do more.
 *
 * Two rules from the canvas that are easy to lose. A tile for something that does not exist yet
 * is rendered dimmed with "coming soon" rather than hidden — the point is to show the shape of
 * the app, and a screen that grows new tiles month by month is a better promise than one that
 * pretends it was always this size. But `Dialogue` is HIDDEN, not dimmed, because it is gated on
 * `capabilities.spokenDialogue`, which is a fact about whether Georgian speech recognition works
 * at all; advertising it before the STT bake-off says it is usable would be a promise the app
 * cannot keep.
 */

const GRID_GAP = 11;

export function Practice() {
  const { me, entitled } = useStudy();
  const profile = me.profiles[0] ?? null;
  const capabilities = me.capabilities[profile?.targetLang ?? 'ka'];

  const due = useQuery({
    queryKey: ['review-summary', profile?.id],
    queryFn: () => api.reviewSummary(profile!.id),
    enabled: !!profile,
  });
  const lessons = useQuery({ queryKey: ['lesson-index'], queryFn: () => api.lessonIndex() });

  // 'unmet' means "no graded axis yet", NOT "unread": a `lesson_viewed` seeds a memory_state row
  // that stays at stage 'new', so a lesson already read still reads unmet. Nothing on the wire
  // distinguishes the two, so the tile says "next:" rather than "you have not read:".
  const nextLesson = lessons.data?.find((r) => r.state === 'unmet')?.lesson.title ?? null;

  const dueCount = due.data?.dueCount ?? null;
  const nothingYet = dueCount === 0;

  return (
    <div style={{ padding: '16px 16px 0' }}>
      <span style={{ fontSize: 17, fontWeight: 600, color: 'var(--nn-text-strong)' }}>
        Practice
      </span>
      <div style={{ marginTop: 3, fontSize: 11.5, color: 'var(--nn-muted)' }}>
        more than the bite — start anything
      </div>

      {!entitled && (
        <Text fz={11.5} c="var(--nn-muted)" mt={10}>
          Your trial has ended — practice is part of the paid layer.
        </Text>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: GRID_GAP,
          marginTop: 16,
          alignItems: 'start',
        }}
      >
        <PracticeTile
          icon={<span style={{ fontSize: 14 }}>ა</span>}
          tint="neutral"
          title="Read a story"
          desc="generated at your edge"
          minutes="~5 min"
          to="/read"
          disabled={!entitled}
        />
        <PracticeTile
          icon={<IconReplay />}
          tint="sky"
          title="Review"
          desc={
            due.isPending
              ? ' '
              : dueCount === null
                ? 'nothing to review yet'
                : dueCount === 0
                  ? 'nothing due right now'
                  : `${dueCount} due now`
          }
          // 40 seconds a prompt, the same estimate the bite composer uses, capped at a run.
          minutes={
            dueCount ? `~${Math.max(1, Math.ceil(Math.min(dueCount, 12) * 0.67))} min` : undefined
          }
          to="/review"
          disabled={!entitled || !dueCount}
        />
        <PracticeTile
          icon={<IconEar />}
          tint="ember"
          title="Listen & repeat"
          desc="hear it, say it, hear yourself"
          minutes="~3 min"
          to="/review?kinds=listen_repeat"
          disabled={!entitled}
        />
        <PracticeTile
          icon={<span style={{ fontSize: 13 }}>გ</span>}
          tint="neutral"
          title="Grammar"
          desc={nextLesson ? `next: ${nextLesson}` : ' '}
          minutes="~3 min"
          to="/words?segment=grammar"
          disabled={!entitled}
        />
        {/* M6c-i turned this one on. No `disabled={!entitled}`, unlike its neighbours:
            `compositionList` is `session`, so a learner whose trial has ended can still open
            the journal and read what they wrote — what disables for them is the New entry
            button on the list itself. Dimming the tile would lock them out of their own diary
            over a subscription. */}
        <PracticeTile
          icon={<IconPencil />}
          tint="neutral"
          title="Journal"
          desc="write; corrections inline"
          minutes="~10 min"
          to="/journal"
        />
        {/* M6c-ii turned this one on, and it is un-dimmed for the same reason the Journal tile
            is: `conversationList` is `session`, so an unentitled learner can still open the
            history and re-read the corrections from a conversation they already had. What their
            subscription gates is starting a new one, and the New chat button on `/chat` is what
            says so. */}
        <PracticeTile
          icon={<IconChat />}
          tint="neutral"
          title="Text chat"
          desc="texting register, with help"
          minutes="~8 min"
          to="/chat"
        />
        {/* Dialogue is absent, not dimmed, until the STT spike says Georgian speech is usable. */}
        {capabilities?.spokenDialogue && (
          <PracticeTile
            icon={<IconMic />}
            tint="neutral"
            title="Dialogue"
            desc="spoken, with a partner"
            minutes="~6 min"
            to="/dialogue"
            disabled={!entitled}
          />
        )}
      </div>

      {nothingYet && entitled && (
        <Text fz={11.5} c="var(--nn-muted)" mt={14}>
          Nothing to review yet — save a few lines or watch something.
        </Text>
      )}
    </div>
  );
}

type Tint = 'ember' | 'sky' | 'neutral';
const TINTS: Record<Tint, { background: string; color: string }> = {
  ember: {
    background: 'color-mix(in srgb, var(--nn-accent) 14%, transparent)',
    color: 'var(--nn-accent)',
  },
  sky: {
    background: 'color-mix(in srgb, var(--nn-link) 13%, transparent)',
    color: 'var(--nn-link)',
  },
  neutral: {
    background: 'color-mix(in srgb, var(--nn-text) 6%, transparent)',
    color: 'color-mix(in srgb, var(--nn-text) 80%, var(--nn-muted))',
  },
};

/**
 * One launcher tile (`11a`): a 30 px icon square, a title, a line of plain description and the
 * rough length. `soon` and `disabled` look the same — .55 opacity, no link — because to the
 * learner they are the same thing: not now. They differ only in the second line.
 */
function PracticeTile({
  icon,
  tint = 'neutral',
  title,
  desc,
  minutes,
  to,
  disabled,
  soon,
}: {
  icon: ReactNode;
  tint?: Tint;
  title: string;
  desc: string;
  minutes?: string;
  to?: string;
  disabled?: boolean;
  soon?: boolean;
}) {
  const inert = soon || disabled || !to;
  const body = (
    <>
      <span
        aria-hidden
        style={{
          width: 30,
          height: 30,
          borderRadius: 8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          ...TINTS[tint],
        }}
      >
        {icon}
      </span>
      <span style={{ fontSize: 13.5, color: 'var(--nn-text-strong)', marginTop: 4 }}>{title}</span>
      <span style={{ fontSize: 11, lineHeight: 1.45, color: 'var(--nn-muted)' }}>
        {soon ? 'coming soon' : desc}
      </span>
      {minutes && !soon && (
        <span style={{ fontSize: 10.5, color: 'var(--nn-faint)' }}>{minutes}</span>
      )}
    </>
  );
  const shell: React.CSSProperties = {
    background: 'var(--nn-panel)',
    border: '1px solid var(--nn-hairline)',
    borderRadius: 10,
    padding: 14,
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    textDecoration: 'none',
    color: 'inherit',
    ...(inert ? { opacity: 0.55 } : {}),
  };
  return inert ? (
    <div style={shell} aria-disabled>
      {body}
    </div>
  ) : (
    <Link to={to!} style={shell}>
      {body}
    </Link>
  );
}

const S = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.8 } as const;

const IconEar = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" {...S} strokeLinecap="round" aria-hidden>
    <path d="M4 14a8 8 0 0 1 16 0" />
    <rect x="3.2" y="14" width="4" height="6" rx="1.6" fill="currentColor" stroke="none" />
    <rect x="16.8" y="14" width="4" height="6" rx="1.6" fill="currentColor" stroke="none" />
  </svg>
);
const IconPencil = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    {...S}
    strokeWidth={1.7}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M16.5 4.5l3 3L8 19H5v-3L16.5 4.5z" />
  </svg>
);
const IconChat = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    {...S}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M20 12a8 8 0 1 0-3.1 6.3L20 19l-.8-3.1A8 8 0 0 0 20 12z" />
  </svg>
);
const IconMic = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" {...S} strokeLinecap="round" aria-hidden>
    <rect x="9" y="3.5" width="6" height="11" rx="3" />
    <path d="M5.5 11.5a6.5 6.5 0 0 0 13 0" />
    <line x1="12" y1="18" x2="12" y2="21" />
  </svg>
);
